import environment from '../constants/environment'
import Router from 'next/router'
import logout from '../logic/logout'

const DOMAIN = process.env.NEXT_PUBLIC_BACKEND_DOMAIN
const ENVIRONMENT = process.env.NEXT_PUBLIC_ENVIRONMENT
const USE_TEST_DATA = false
const lessonDetailsTestResponse = require('tests/testData/lessonDetails/lessonDetailsFillInTheBlankQuestion.json')

const DONT_REDIRECT_ON_401_ENDPOINT = ['/login', '/login/google-sso', '/login/sso', '/users/changePassword', '/notificationTokens/logout', '/users/setNotificationPreference', '/users/getNotificationPreferences']

export const createAccount = async (
  {
    code,
    emailAddress,
    firstName,
    lastName,
    password,
    teacher = false,
    schoolName = null,
    jobTitle = null,
    district = null,
    county = null,
    state = null,
    country = null,
    educatorType = null,
    ssoToken = null,
    ssoType = null,
    personal= false,
  }: {
    code: string
    emailAddress: string
    firstName: string
    lastName: string
    password: string
    teacher?: boolean
    schoolName?: string | null
    jobTitle?: string | null
    district?: string | null
    county?: string | null
    state?: string | null
    country?: string | null
    educatorType?: string | null
    ssoToken?: string | null
    ssoType?: string | null
    personal?: boolean
  },
): Promise<[boolean, string]> => {
  const data = {
    code,
    emailAddress,
    firstName,
    lastName,
    password,
    teacher,
    schoolName,
    jobTitle,
    district,
    county,
    state,
    country,
    educatorType,
    personal,
    ssoToken,
    ssoType,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users', 'POST', data)
  } catch (e) {
    throw new Error('There was an error creating your account. Please try again.')
  }

  if (response.status === 201) {
    return [true, (await response.json()).jwtToken]
  }

  let responseJson: any
  try {
    responseJson = await response.json()
  } catch {
    throw new Error(`There was an error creating your account. Please try again.${!personal ? ' If the issue persists, please let your teacher know.' : ''}`)
  }
  throw new Error(responseJson.code)
}

export const getUser = async (): Promise<any> => {
  let response: Response
  try {
    response = await makeAPIRequest('/users/self', 'GET')
    if (response.status !== 200) {
      throw new Error()
    }
    return await response.json()
  } catch (e) {
    throw new Error(e as string)
  }
}

export const getInitialLessons = async (classroomId: number): Promise<any> => {
  let response: Response
  if (ENVIRONMENT === environment.DEV && USE_TEST_DATA) return require('tests/testData/lessons/shortenedCourse/shortenedCourse.json')
  try {
    response = await makeAPIRequest(`/userLessons/${classroomId}/lessons`, 'GET')
    if (response.status !== 200) {
      throw new Error()
    }
    return await response.json()
  } catch (e) {
    throw new Error()
  }
}

export const getDetailedLesson = async (classroomId: number, lessonId: string, startOver: boolean = false, cachedJwt: string | null = null): Promise<any> => {
  let response: Response
  if (ENVIRONMENT === environment.DEV && USE_TEST_DATA) return lessonDetailsTestResponse
  try {
    response = await makeAPIRequest(`/userLessons/${classroomId}/lessons/${lessonId}?startOver=${startOver}`, 'GET', {}, {}, cachedJwt)
    if (response.status !== 200) {
      throw new Error()
    }
    return await response.json()
  } catch (e) {
    throw new Error()
  }
}

export const getFullLessonContent = async (classroomId: number, lessonId: string): Promise<any> => {
  let response: Response
  try {
    response = await makeAPIRequest(`/userLessons/${classroomId}/lessons/${lessonId}/fullContent`, 'GET')
    if (response.status !== 200) {
      throw new Error()
    }
    return await response.json()
  } catch (e) {
    throw new Error(e as string)
  }
}

export const deleteUser = async (): Promise<void> => {
  try {
    await makeAPIRequest('/users/self', 'DELETE')
  } catch (e) {
    throw new Error()
  }
}
export const postUpdateUserName = async ({ firstName, lastName }: { firstName: string, lastName: string }): Promise<void> => {
  const data = {
    firstName,
    lastName,
  }

  try {
    await makeAPIRequest('/users/self', 'POST', data)
  } catch (e) {
    throw new Error()
  }
}

export const postChangePassword = async ({ currentPassword, newPassword }: { currentPassword: string, newPassword: string }): Promise<boolean> => {
  const data = {
    currentPassword,
    password: newPassword,
  }

  try {
    const response = await makeAPIRequest('/users/changePassword', 'POST', data)
    if (response.status === 200) {
      return true
    } else if (response.status === 401) {
      return false
    } else {
      throw new Error()
    }
  } catch (e) {
    throw new Error()
  }
}

export const postUpdateDailyBeatPreference = async ({ preferenceKey }: { preferenceKey: string }): Promise<void> => {
  const data = {
    preference: preferenceKey,
  }

  try {
    await makeAPIRequest('/users/preference', 'POST', data)
  } catch (e) {
    throw new Error()
  }
}

export const getDailyBeatPreference = async (): Promise<string> => {
  try {
    const response = await makeAPIRequest('/users/preference', 'GET')
    const json = await response.json()
    return json.key
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postUpdateLessonProgress = async (
  {
    lessonId,
    sectionId,
    itemId,
    answerId,
    answerItemId,
    classroomId,
    longAnswer,
    classroomMode=false,
    excludedStudentIds=[],
    cachedJwt=null
  }: {
    lessonId: string
    sectionId: string
    itemId: string
    answerId: string | null
    answerItemId: string | null
    classroomId: number
    longAnswer: string | null
    classroomMode?: boolean
    excludedStudentIds?: string[]
    cachedJwt?: string | null
  },
): Promise<any> => {
  let response: Response
  if (ENVIRONMENT === environment.DEV && USE_TEST_DATA) return require('tests/testData/userLessonsPost/responseWithoutAnswerItemAnimatedCard.json')

  const data: Record<string, any> = {
    lessonId,
    sectionId,
    itemId,
    answerId,
    answerItemId,
    classroomId,
    longAnswer,
  }

  try {
    if (!classroomMode) {
      response = await makeAPIRequest(`/userLessons/`, 'POST', data, {}, cachedJwt)
    } else {
      data.excludedStudentIds = excludedStudentIds
      response = await makeAPIRequest(`/userLessons/classroomMode`, 'POST', data, {}, cachedJwt)
    }
    if (![200, 204].includes(response.status)) {
      throw new Error(String(response.status))
    } else if (response.status === 200) {
      return await response.json()
    } else {
      return {
        completed: true,
      }
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const putUserTimezone = async ({ timezone }: { timezone: string }): Promise<boolean> => {
  const data = {
    timezone,
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/timezone', 'PUT', data)
    if (response.status !== 200) {
      throw new Error(response.statusText)
    }
    return true
  } catch (e) {
    const errorString = (e as Error).toString()
    if (errorString.includes('Auth token expired')) {
      console.log('Session expired, user has been logged out')
      return false
    }

    throw new Error(e as string)
  }
}

export const acceptLatestTerms = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/acceptLatestTerms', 'POST')
    if (response.status !== 200) {
      throw new Error('Could not accept terms')
    }
  } catch (e) {
    throw new Error('Could not accept terms')
  }
}

export const getFullDailyBeatContent = async (classroomId: number, dailyBeatId: string, isTeacher: boolean): Promise<any> => {
  let response: Response
  try {
    response = await makeAPIRequest(`/classrooms/${classroomId}/dailyBeats/${dailyBeatId}/fullContent?isTeacher=${isTeacher}`, 'GET')
    if (response.status !== 200) {
      throw new Error()
    }
    return await response.json()
  } catch (e) {
    throw new Error(e as string)
  }
}

export const getInitialDailyBeats = async (classroomId: number): Promise<any> => {
  let response: Response
  if (ENVIRONMENT === environment.DEV && USE_TEST_DATA) return require('tests/testData/dailyBeats/shortenedCourse/shortenedCourse.json')
  try {
    response = await makeAPIRequest(`/classrooms/${classroomId}/dailyVideosV2`, 'GET')
    if (response.status !== 200) {
      throw new Error()
    }
    return await response.json()
  } catch (e) {
    throw new Error()
  }
}

export const updateColorSchemePreferenceWithServer = async (
  preference: string,
): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/updateColorSchemePreference', 'POST', {
      preference
    })
    if (response.status !== 200) {
      throw new Error('Error updating color scheme')
    }
  } catch {
    throw new Error('Error updating color scheme')
  }
}

// NOTE: deprecated for new courses, only kept for backwards compatibility with previous 10 and 21-day courses
export const getNextDailyBeatItem = async (
  {
    classroomId,
    completedItemId,
    answerId,
    dailyBeatId,
    isTeacher,
    excludedStudentIds=[],
    classroomMode=false,
    cachedJwt=null
  }: {
    classroomId: number
    completedItemId: string | null
    answerId: string | null
    dailyBeatId: string
    isTeacher: boolean
    excludedStudentIds?: number[]
    classroomMode?: boolean
    cachedJwt?: string | null
  },
): Promise<any> => {
  const body: Record<string, any> = {
    completedItemId,
    answerId,
    dailyBeatId,
    isTeacher
  }

  try {
    let response: Response
    if (!classroomMode) {
      response = await makeAPIRequest(`/classrooms/${classroomId}/dailyVideosV2`, 'POST', body, {}, cachedJwt)
    } else {
      body.excludedStudentIds = excludedStudentIds
      response = await makeAPIRequest(`/classrooms/${classroomId}/dailyVideosV2/classroomMode`, 'POST', body, {}, cachedJwt)
    }
    if (response.status === 201) {
      return {
        completed: true,
      }
    } else if (response.status === 200) {
      return response.json()
    } else {
      throw new Error()
    }
  } catch (e) {
    throw new Error()
  }
}

export const getFocusSeshLogs = async (): Promise<any> => {
  let response: Response
  try {
    response = await makeAPIRequest('/focusSeshLogs', 'GET')
    if (response.status !== 200) {
      throw new Error('Error retrieving focus sesh logs')
    }
    return (await response.json())
  } catch (e) {
    throw new Error(e as string)
  }
}

export const getFocusSeshPresets = async (): Promise<any> => {
  let response: Response
  try {
    response = await makeAPIRequest('/focusSeshPresets', 'GET')
    if (response.status !== 200) {
      throw new Error('Error retrieving focus sesh presets')
    }
    const responseJson = await response.json()
    return responseJson
  } catch (e) {
    throw new Error(e as string)
  }
}

// eslint-disable-next-line
export const makeAPIRequest = async (endpoint: string, method: string, data: Record<string, any> = {}, query: Record<string, any> = {}, cachedJwt?: string | null): Promise<Response> => {
  let jwt = localStorage.getItem('access_token')
  if (!jwt) {
    jwt = cachedJwt || null
  }

  const options: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
  }

  if (method !== 'GET') {
    options.body = JSON.stringify(data)
  }
  const response = await fetch(`${DOMAIN}${endpoint}`, options)

  if (!DONT_REDIRECT_ON_401_ENDPOINT.includes(endpoint) && [403, 401].includes(response.status)) {
    logout(Router)
    throw new Error('Auth token expired')
  }
  return response
}

export const getCurrentStudentInterfaceVersion = async (): Promise<any> => {
  try {
    const response = await makeAPIRequest('/users/currentStudentInterfaceVersion', 'GET')
    if (response.status !== 200) {
      throw new Error('There was an error retrieving the current student interface version')
    }
    const data = await response.json()
    return data
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postLastSeenStudentInterfaceVersion = async (version: number): Promise<void> => {
  try {
    const data = { version }
    const response = await makeAPIRequest('/users/setLastSeenStudentInterfaceVersion', 'POST', data)
    if (response.status !== 200) {
      throw new Error('There was an error setting the last seen student interface version')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const joinAnotherClassroom = async (code: string, userHasCourseCompletion: boolean): Promise<void> => {
  try {
    const response = await makeAPIRequest(`/classrooms/join/${code}/${userHasCourseCompletion}`, 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error joining the classroom')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const convertPersonalToStudentAccountAndJoinClassroom = async (classroomCode: string, userHasCourseCompletion: boolean, firstName: string, lastName: string): Promise<void> => {
  try {
    const data = {
      classroomCode,
      userHasCourseCompletion,
      firstName,
      lastName,
    }
    const response = await makeAPIRequest(`/users/convertToStudentAccount`, 'POST', data)
    if (response.status !== 200) {
      throw new Error('There was an error joining the classroom and converting to a student account')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postDismissFocusCoachConfidentialityMessage = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/dismissFocusCoachConfidentialityMessage', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const updateRecentSoundtracks = async (newSoundtrack: string): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/updateRecentSoundtracks', 'POST', { newSoundtrack })
    if (response.status !== 200) {
      throw new Error('There was an error updating recent soundtracks')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postDismissPiPFeatureTag = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/dismissPiPFeatureTag', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error dismissing the PiP feature tag')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postCompleteStudentOrientation = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/completeStudentOrientation', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error completing student orientation')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postDismissCourseProgressResetNotice = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/dismissCourseProgressResetNotice', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error dismissing the course progress reset notice')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postDismissPersonalReflectionConfidentialityMessage = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/dismissPersonalReflectionConfidentialityMessage', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postDismissVoiceGuidanceReturningUserDialog = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/dismissVoiceGuidanceReturningUserDialog', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const getLatestRequiredVersion = async (): Promise<number> => {
  try {
    const response = await makeAPIRequest('/latestWebAppVersion', 'GET')
    return (await response.json()).requiredVersion
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postUpdateLessonNarrationEnabled = async (lessonNarrationEnabled): Promise<void> => {
  try {
    const data = {
      lessonNarrationEnabled
    }
    const response = await makeAPIRequest('/users/setLessonNarrationEnabled', 'POST', data)
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postUpdateLessonSoundtrackEnabled = async (lessonSoundtrackEnabled): Promise<void> => {
  try {
    console.log('lessonSoundtrackEnabled', lessonSoundtrackEnabled)
    const data = {
      lessonSoundtrackEnabled
    }
    const response = await makeAPIRequest('/users/setLessonSoundtrackEnabled', 'POST', data)
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}
